'use client'

import React, { useCallback, useMemo } from 'react'
import Image from 'next/image'
import { cn, isDefined, useProductImageCarousel } from '@segway/core'
import Fade from 'embla-carousel-fade'

import {
  Carousel,
  CarouselItem,
  // MagnifiedArea,
  // MagnifierImage,
  // MagnifierProvider,
  Wishlist,
} from '@/components'
import { CarouselPlugin } from '@/components/carousel/Carousel'

import { useProduct, useProductData } from '../../ProductProvider'

const ProductImage: React.FC = () => {
  const { isWishlist, responsive, productAffirmData, toggleWishlist } = useProduct()
  const product = useProductData()

  const { activeProductImageIndex, handleProductImageCarouselChange } = useProductImageCarousel()

  const bestInventoryIcon = useMemo(() => {
    return product?.formily_content?.find((item) => item?.code === 'image_icon')?.data
  }, [product?.formily_content])

  const handleWishlistToggle = useCallback(() => {
    if (product?.sku) {
      toggleWishlist(product.sku)
    }
  }, [product, toggleWishlist])

  const mediaGallery = useMemo(() => {
    return product?.media_gallery?.filter((item) => !item?.disabled)
  }, [product])

  return (
    <div className="relative">
      {responsive && responsive.xl && (
        // <MagnifierProvider>
        <Carousel
          plugins={[Fade()] as unknown as CarouselPlugin}
          autoPlay={false}
          loop={true}
          showArrows={(mediaGallery?.length || 0) > 1}
          showThumbnails={true}
          isHoverShowArrows={true}
          onSlideChange={handleProductImageCarouselChange}
          thumbnailsData={mediaGallery?.map((item) => item?.url || '')}
          arrowLeftContainerClassName="-translate-y-[calc(50%+86px)]"
          arrowRightContainerClassName="-translate-y-[calc(50%+86px)]"
          indicatorsContainerClassName="bottom-[109px] gap-1 xl:gap-3"
          indicatorsActiveItemClassName="bg-black/40 h-[6px] xl:h-2"
          indicatorsItemClassName="bg-black/15 h-[6px] w-[6px] xl:w-2 xl:h-2"
          className="w-full min-w-[600px] max-w-[900px]"
          thumbnailContainerClassName="!bottom-[110px]">
          {!!mediaGallery?.length ? (
            mediaGallery.map((item, index) => {
              return (
                <CarouselItem key={index} className="flex h-[375px] justify-center xl:h-[796px]">
                  <div className={`relative flex h-[796px] w-full justify-center text-[0px]`}>
                    {/* <MagnifierImage smallImage={item?.url || ''} /> */}
                    <Image
                      src={item?.url || ''}
                      width={600}
                      height={796}
                      alt={item?.label || ''}
                      className="h-[600px] w-[600px] object-cover"
                    />
                  </div>
                </CarouselItem>
              )
            })
          ) : (
            <CarouselItem className="flex h-[796px] justify-center rounded-lg bg-gray-1" />
          )}
        </Carousel>
        // <MagnifiedArea
        //   largeImage={product?.media_gallery?.[activeProductImageIndex]?.url || ''}
        //   magnifiedScale={0.6}
        // />
        // </MagnifierProvider>
      )}

      {responsive && !responsive.xl && (
        <Carousel
          className="h-full"
          autoPlay={false}
          showArrows={false}
          showThumbnails={false}
          thumbnailsData={mediaGallery?.map((item) => item?.url || '')}
          indicatorsContainerClassName="bottom-[25px] gap-1 xl:gap-3"
          indicatorsActiveItemClassName="bg-black/40 h-[6px] xl:h-2"
          indicatorsItemClassName="bg-black/15 h-[6px] w-[6px] xl:w-2 xl:h-2">
          {!!mediaGallery?.length ? (
            mediaGallery.map((item, index) => {
              return (
                <CarouselItem
                  key={index}
                  className="relative flex justify-center rounded-lg pb-[100%] xl:h-[796px]">
                  <Image
                    src={item?.url || ''}
                    alt={item?.label || ''}
                    fill
                    style={{ objectFit: 'cover' }}
                  />
                </CarouselItem>
              )
            })
          ) : (
            <CarouselItem className="flex h-[375px] justify-center rounded-lg bg-gray-1" />
          )}
        </Carousel>
      )}

      <div
        onClick={handleWishlistToggle}
        className="absolute right-4 top-4 flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-gray-0 bg-white xl:h-[76px] xl:w-[76px] xl:border-none">
        {responsive && responsive.xl && (
          <Wishlist width={29} height={25} color={isWishlist ? '#DA291C' : ''} />
        )}

        {responsive && !responsive.xl && (
          <Wishlist width={17} height={15} color={isWishlist ? '#DA291C' : ''} />
        )}
      </div>

      {isDefined(bestInventoryIcon) && (
        <Image
          src={bestInventoryIcon || ''}
          alt="bestInventoryIcon"
          width={60}
          height={60}
          style={{ objectFit: 'contain' }}
          className={cn(
            'absolute left-[10px] top-[11px] flex h-[100px] w-[100px] cursor-pointer items-center justify-center rounded-full bg-transparent xl:left-[28.5px] xl:top-[28.5px]',
            responsive && responsive?.xl ? 'h-[100px] w-[100px]' : 'h-[56px] w-[56px]',
          )}
        />
      )}

      {isDefined(productAffirmData?.product_image) &&
        productAffirmData?.product_image !== '' &&
        activeProductImageIndex < 3 && (
          <Image
            src={productAffirmData?.product_image || ''}
            alt="affirm"
            width={60}
            height={60}
            style={{ objectFit: 'contain' }}
            className={cn(
              'absolute left-[10px] flex h-[100px] w-[100px] items-center justify-center rounded-md bg-transparent xl:left-[28.5px]',
              responsive && responsive?.xl ? 'h-[100px] w-[100px]' : 'h-[56px] w-[56px]',
              isDefined(bestInventoryIcon)
                ? 'top-[71px] xl:top-[140.5px]'
                : 'top-[11px] xl:top-[28.5px]',
            )}
          />
        )}
    </div>
  )
}

export default ProductImage
